import { MICROKNOWLEDGE_MAX_LENGTHS } from '@sparted/shared-library';
import React, { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { t } from 'i18next';

import SMicroKnowledgeListItem from 'Components/structural/SMicroKnowledgeListItem/SMicroKnowledgeListItem/SMicroKnowledgeListItem';
import UMediaPicker, { PickerSelectedImage } from 'Components/unit/UMediaPicker/UMediaPicker';
import SSubtleInput from 'Components/structural/SSubtleInput/SSubtleInput';
import UIcon from 'Components/unit/UIcon/UIcon';
import STooltip from 'Components/structural/STooltip/STooltip';
import UDropdownBox from 'Components/unit/UDropdownBox/UDropdownBox';
import { type IconName } from 'Components/foundation/icons';
import { type MicroKnowledgeImage } from 'Pages/Courses/utils/mk-mapped-linked-list';
import { COLORS } from 'Components/foundation';

import { type MicroKnowledgeItemSharedProps } from '../types/shared-props';
import styles from './SImageMicroKnowledgeItem.style';

const MICROKNOWLEDGE_COLOR = COLORS.WHITE.DEFAULT;

const DEFAULT_MENU_COLOR = COLORS.BLACK.DEFAULT;
const ACTIVE_MENU_COLOR = COLORS.PRIMARY.DEFAULT;
const BACKGROUND_ACTIVE_MENU_COLOR = '#E9F1FF';

type SettingsItem = {
  id: number;
  type: 'vertical-align' | 'horizontal-align';
  icon: IconName;
  label: string;
  onClick: (item: SettingsItem) => void;
};

export type SImageMicroKnowledgeItemProps = MicroKnowledgeItemSharedProps & {
  imageItem: MicroKnowledgeImage;
  isChild?: boolean;
  backgroundColor?: CSSProperties['backgroundColor'];
  hideContent?: boolean;
  onChange: (updatedValue: Partial<MicroKnowledgeImage>) => void;
};

/**
 * SImageMicroKnowledgeItem component
 * Props:
 * - imageItem: MicroKnowledgeImage object
 * - onChange: Callback which updates the changes,
 * - isChild?: boolean which states whether this microknowledge is a child or not,
 * - backgroundColor?: CSSProperties['backgroundColor'],
 * - hideContent: boolean that states whether the content input (with the image and all options) is shown or not
 */
export const SImageMicroKnowledgeItem = ({
  imageItem,
  backgroundColor = MICROKNOWLEDGE_COLOR,
  isChild = false,
  options,
  hideContent = false,
  onAdd,
  onChange,
}: SImageMicroKnowledgeItemProps) => {
  const settingsRef = useRef<HTMLDivElement>(null);

  const [automaticScroll, setScroll] = useState(false);
  const [isFocus, setIsFocus] = useState(false);
  const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false);

  const { id, image, title, content, verticalAlign } = imageItem;
  const { url: imageUrl } = image || {};

  const imageLabel = useMemo(
    () => (!imageUrl ? t('courses:course_edition.upload_image') : t('courses:course_edition.update_image')),
    [image],
  );

  const titleContent = useMemo(() => title || '', [title]);

  const settingsItems: SettingsItem[] = useMemo(
    () => [
      {
        id: 1,
        type: 'vertical-align',
        icon: 'position-top',
        label: t('courses:course_edition.settings.vertical_align.top'),
        onClick: () => onChange({ verticalAlign: 'top' }),
      },
      {
        id: 2,
        type: 'vertical-align',
        icon: 'position-center',
        label: t('courses:course_edition.settings.vertical_align.center'),
        onClick: () => onChange({ verticalAlign: 'center' }),
      },
      {
        id: 3,
        type: 'vertical-align',
        icon: 'position-bottom',
        label: t('courses:course_edition.settings.vertical_align.bottom'),
        onClick: () => onChange({ verticalAlign: 'bottom' }),
      },
    ],
    [onChange],
  );

  const onFocusChange = useCallback(() => {
    setIsFocus(true);
  }, []);

  const onBlurChange = useCallback(() => {
    setScroll(true);
    setIsFocus(false);
  }, []);

  const handleAdd = useCallback(() => onAdd?.(id), [onAdd, id]);

  const handleTitleChange = useCallback((newValue: string) => onChange({ title: newValue }), [onChange]);

  const handleContentChange = useCallback((newValue: string) => onChange({ content: newValue }), [onChange]);

  const handleImageChange = useCallback(
    (image: PickerSelectedImage) => {
      onChange?.({
        image: {
          id: image.id,
          url: image.url,
        },
      });
    },
    [onChange],
  );

  const textareaMaxHeight = isFocus ? '200px' : '43px';

  useEffect(() => {
    // reset scroll state
    setScroll(false);
  }, [automaticScroll]);

  const handleSettingsMenuClick = useCallback(() => {
    setIsSettingsMenuOpen((prev) => !prev);
  }, []);

  const handleOutsideClickSettingsMenu = useCallback((event: MouseEvent) => {
    if (settingsRef.current && !settingsRef.current.contains(event.target as Node)) {
      setIsSettingsMenuOpen(false);
    }
  }, []);

  const handleLinkClick = useCallback(() => {
    // TODO: open link modal in future batch
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClickSettingsMenu, false);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClickSettingsMenu, false);
    };
  }, [handleOutsideClickSettingsMenu]);

  const renderSettingsItems = useCallback(
    (item: SettingsItem) => {
      switch (item.type) {
        case 'vertical-align': {
          const isActive = item.icon.includes(verticalAlign);
          return (
            <div key={`settings-item-${id}-${item.id}`}>
              <UIcon
                data-tooltip-id={`settings-item-tooltip-${id}-${item.id}`}
                name={item.icon}
                size={24}
                onClick={() => item.onClick(item)}
                color={isActive ? ACTIVE_MENU_COLOR : DEFAULT_MENU_COLOR}
                style={{
                  ...styles.settingsIcon,
                  backgroundColor: isActive ? BACKGROUND_ACTIVE_MENU_COLOR : 'transparent',
                }}
              />
              {/* The short delay is necessary to avoid a react-tooltip bug with the tooltip rendered shortly in the default position */}
              <STooltip id={`settings-item-tooltip-${id}-${item.id}`} content={item.label} position="left" delay={1} />
            </div>
          );
        }
        default:
          return null;
      }
    },
    [verticalAlign, id],
  );

  return (
    <SMicroKnowledgeListItem
      id={id}
      isChild={isChild}
      contextMenuOptions={options}
      backgroundColor={backgroundColor}
      onAdd={handleAdd}
    >
      <SSubtleInput
        inputType="input"
        style={styles.title}
        value={titleContent}
        placeholder={t('courses:course_edition.type_title')}
        maxLength={MICROKNOWLEDGE_MAX_LENGTHS.IMAGE.MAX_TITLE_LENGTH}
        onChange={handleTitleChange}
        debounced
      />
      {!hideContent && (
        <UMediaPicker
          size="XS"
          dropArea
          image={imageUrl}
          style={styles.image}
          textLink={imageLabel}
          textLinkStyle={styles.imageLabel}
          textLinkTypography="BODY2"
          media={{ mediaType: 'image', onChange: handleImageChange }}
        />
      )}
      {!hideContent && (
        <SSubtleInput
          inputType="textarea"
          style={styles.content}
          value={content}
          placeholder={t('courses:course_edition.type_image_cation')}
          maxLength={MICROKNOWLEDGE_MAX_LENGTHS.IMAGE.MAX_CONTENT_LENGTH}
          maxHeight={textareaMaxHeight}
          onChange={handleContentChange}
          onFocus={onFocusChange}
          onBlur={onBlurChange}
          forceTextareaScroll={automaticScroll}
          debounced
        />
      )}
      {!hideContent && (
        <div style={styles.iconsContainer}>
          <div ref={settingsRef} style={styles.settingsContainer}>
            <UIcon
              data-tooltip-id="settings"
              name="settings"
              type="accentuated"
              size={24}
              onClick={handleSettingsMenuClick}
            />
            <STooltip id="settings" content={t('courses:course_edition.settings.title')} position="top" />
            {isSettingsMenuOpen && (
              <div style={styles.settingsDropdownContainer} onClick={handleSettingsMenuClick}>
                <UDropdownBox items={settingsItems} renderItem={renderSettingsItems} style={styles.settingsDropdown} />
              </div>
            )}
          </div>
          <div style={styles.linkContainer}>
            <UIcon data-tooltip-id="link" name="link" type="accentuated" size={24} onClick={handleLinkClick} />
            <STooltip id="link" content={t('courses:course_edition.settings.link')} position="top" />
          </div>
        </div>
      )}
    </SMicroKnowledgeListItem>
  );
};

export default SImageMicroKnowledgeItem;
